import React from 'react';
import styled from '@grebban/style-system-react';
import Text from 'components/Text';
import Button from 'components/Button';
import { OVERLAY_GRID_COLUMNS } from 'components/Header/utils';
import { type SubmenuSection } from 'components/Header/types';

const ITEMS_START_COLUMN = 5;

const SectionBlock = styled('div')`
    display: grid;
    grid-template-columns: repeat(${OVERLAY_GRID_COLUMNS}, 1fr);
    column-gap: var(--grid-gap);
    grid-column: 1 / -1;
    margin-bottom: 0;
`;

const SectionTitleText = styled(Text)`
    display: block;
    grid-column: 1 / ${ITEMS_START_COLUMN};
    color: var(--ui-primary-aa);
    margin-bottom: 12px;
`;

/** Grid only for column split (1 or 2); items sit in the same cell and stack vertically. */
const ItemsList = styled('div')<{ $isTwoColumns: boolean; $hasTitle: boolean }>`
    grid-column: ${({ $hasTitle }) => ($hasTitle ? `${ITEMS_START_COLUMN} / -1` : '1 / -1')};
    display: grid;
    grid-template-columns: ${({ $isTwoColumns }) => ($isTwoColumns ? '1fr 1fr' : '1fr')};
    column-gap: var(--grid-gap);
`;

/** One column of links – vertical stack within one grid cell. */
const ItemsColumn = styled('div')`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const ItemLink = styled(Button)`
    display: block;
    text-align: left;
`;

export interface NavigationSubmenuSectionProps {
    title?: string;
    columnLayout: string;
    items: SubmenuSection['items'];
    /** When true, render as single column (e.g. mobile overlay). */
    forceSingleColumn?: boolean;
    /** Called when a link in this section is clicked (e.g. to close the overlay). */
    onCloseOverlay?: () => void;
}

const TYPOGRAPHY_ITEM_COL1 = 'Primary/20_120_510_-3';
const TYPOGRAPHY_ITEM_COL2 = 'Primary/15_120_510_-1';
const TYPOGRAPHY_MOBILE_TITLE = 'Secondary/Mobile/13_120_400';
const TYPOGRAPHY_MOBILE_ITEM = 'Primary/Mobile/18_120_510_-3';

export const NavigationSubmenuSection = ({
    title,
    columnLayout,
    items,
    forceSingleColumn = false,
    onCloseOverlay,
}: NavigationSubmenuSectionProps) => {
    const isTwoColumns = !forceSingleColumn && columnLayout === '2';
    const itemTypography = forceSingleColumn
        ? TYPOGRAPHY_MOBILE_ITEM
        : isTwoColumns
          ? TYPOGRAPHY_ITEM_COL2
          : TYPOGRAPHY_ITEM_COL1;
    const titleTypography = forceSingleColumn ? TYPOGRAPHY_MOBILE_TITLE : 'Secondary/15_120_400';
    const hasTitle = title != null && title !== '';
    const validItems = items.filter((item) => item.link?.url != null && item.link.url !== '');
    const mid = Math.ceil(validItems.length / 2);
    const columnChunks = isTwoColumns ? [validItems.slice(0, mid), validItems.slice(mid)] : [validItems];

    return (
        <SectionBlock>
            {hasTitle && (
                <SectionTitleText as="span" typography={titleTypography}>
                    {title}
                </SectionTitleText>
            )}
            <ItemsList $isTwoColumns={isTwoColumns} $hasTitle={hasTitle}>
                {columnChunks.map((chunk, chunkIndex) => (
                    <ItemsColumn
                        key={
                            chunk[0]
                                ? `${String(
                                      (chunk[0] as Record<string, unknown>).key ?? chunk[0].link?.url ?? chunkIndex,
                                  )}-${chunkIndex}`
                                : `chunk-${chunkIndex}`
                        }
                    >
                        {chunk.map((item, itemIndex) => {
                            const key = `${String((item as Record<string, unknown>).key ?? item.link?.url ?? itemIndex)}-${itemIndex}`;
                            const { url, text } = item.link!;
                            return (
                                <ItemLink
                                    key={key}
                                    theme="textButton"
                                    typography={itemTypography}
                                    url={url}
                                    aria-label={text}
                                >
                                    {text}
                                </ItemLink>
                            );
                        })}
                    </ItemsColumn>
                ))}
            </ItemsList>
        </SectionBlock>
    );
};
